import React, { useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import {
  Box,
  IconButton,
  Tooltip,
  useTheme as useMuiTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import UseLogout from "../../hook/UseLogout";
import useLocalStorageVO from "../Common/UseLocalStorageVO";

const BranchManagerHeader = () => {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const { domain, domainName } = useDomain();
    const muiTheme = useMuiTheme();
    const { logoutProcess } = UseLogout();
    const { loginUser } = useLocalStorageVO();

    // 사용자 메뉴 상태
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const userMenuOpen = Boolean(userMenuAnchor);

    // 사용자 메뉴 열기/닫기
    const handleUserMenuOpen = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    // 내 프로필로 이동
    const handleProfileClick = () => {
        handleUserMenuClose();
        navigate('/profile');
    };

    // 로그아웃 핸들러
    const handleLogout = useCallback(() => {
        handleUserMenuClose();

        Swal.fire({
            title: '로그아웃',
            text: '정말 로그아웃 하시겠습니까?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: muiTheme.palette.primary.main,
            cancelButtonColor: muiTheme.palette.grey[500],
            confirmButtonText: '로그아웃',
            cancelButtonText: '취소'
        }).then((result) => {
            if (result.isConfirmed) {
                logoutProcess();
            }
        });
    }, [muiTheme.palette.primary.main, muiTheme.palette.grey, logoutProcess]);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 24px',
            backgroundColor: muiTheme.palette.background.paper,
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
            {/* 왼쪽: 타이틀 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ 
                    fontWeight: 'bold',
                    color: muiTheme.palette.primary.main
                }}>
                    {domainName} KPI 모니터링
                </Typography>
                {loginUser?.userNm && (
                    <Typography variant="body2" sx={{
                        color: muiTheme.palette.text.secondary
                    }}>
                        {loginUser.userNm}
                    </Typography>
                )}
            </Box>

            {/* 오른쪽: 액션 버튼들 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                {/* 사용자 메뉴 */}
                <Tooltip title="사용자 메뉴">
                    <IconButton
                        onClick={handleUserMenuOpen}
                        size="small"
                        color="inherit"
                        aria-controls={userMenuOpen ? 'user-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={userMenuOpen ? 'true' : undefined}
                    >
                        <AccountCircleIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* 사용자 메뉴 */}
                <Menu
                    id="user-menu"
                    anchorEl={userMenuAnchor}
                    open={userMenuOpen}
                    onClose={handleUserMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'user-button',
                    }}
                    PaperProps={{
                        elevation: 3,
                        sx: {
                            minWidth: 220,
                            mt: 1,
                            '& .MuiMenuItem-root': {
                                px: 2,
                                py: 1.5,
                            },
                        },
                    }}
                >
                    <MenuItem onClick={handleProfileClick}>
                        <ListItemIcon>
                            <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>내 프로필</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>로그아웃</ListItemText>
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );
};

export default BranchManagerHeader;